import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as Tone from 'tone';
import Chat from '../components/Chat';
import Keyboard from '../components/Keyboard';
import styles from './StudioPage.module.css';

const ELEVEN_API_KEY = 'sk_a66a253303c4ec06fd6a7dde79b0ebc9f7568d4ce492c675';
const GROQ_API_KEY = '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';

const SAMPLE_NOTES = ['C3','G3','E4','C5'];
const ELEVEN_DELAY_MS = 400;
const NOTE_SEMITONES = { C:0,'C#':1,D:2,'D#':3,E:4,F:5,'F#':6,G:7,'G#':8,A:9,'A#':10,B:11 };

const noteToMidi = (note) => {
  const match = note.match(/^([A-G]#?)(\d)$/);
  if (!match) return 60;
  const [, name, oct] = match;
  return 12 + Number(oct) * 12 + NOTE_SEMITONES[name];
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const DEFAULT_EFFECTS = { reverb: 0.3, delay: 0.1, volume: 0 };

export default function StudioPage({ onBack }) {
  const [overlay, setOverlay] = useState({ opacity: 1, background: 'white' });
  const [status, setStatus] = useState('idle');
  const [loadingMsg, setLoadingMsg] = useState('');
  const [noteStatuses, setNoteStatuses] = useState({});
  const [instruments, setInstruments] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [pressedKeys, setPressedKeys] = useState(new Set());
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [playbackId, setPlaybackId] = useState(null);
  const [playbackKeys, setPlaybackKeys] = useState(new Set());
  const [effectsState, setEffectsState] = useState([]);

  const samplersRef = useRef({});
  const effectsChainRef = useRef({});
  const recordedRef = useRef([]);
  const recordStartRef = useRef(0);
  const playbackTimersRef = useRef([]);
  const genAudioRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setOverlay({ opacity: 0, background: 'white' }), 50);
    return () => clearTimeout(t);
  }, []);

  const currentEffects = effectsState[currentIdx] ?? DEFAULT_EFFECTS;

  const setCurrentEffect = (key, val) => {
    setEffectsState(prev => {
      const next = [...prev];
      next[currentIdx] = { ...next[currentIdx], [key]: val };
      return next;
    });
  };

  useEffect(() => {
    const chain = effectsChainRef.current[currentIdx];
    if (!chain) return;
    chain.reverb.wet.value = currentEffects.reverb;
    chain.delay.wet.value = currentEffects.delay;
    chain.vol.volume.value = currentEffects.volume;
  }, [currentEffects, currentIdx]);

  const buildEffectsChain = (rev = 0.3, del = 0.1, vol = 0) => {
    const volNode    = new Tone.Volume(vol);
    const reverbNode = new Tone.Reverb({ decay: 2.5, wet: rev });
    const delayNode  = new Tone.FeedbackDelay({ delayTime: '8n', feedback: 0.3, wet: del });
    volNode.connect(reverbNode);
    reverbNode.connect(delayNode);
    delayNode.toDestination();
    return { vol: volNode, reverb: reverbNode, delay: delayNode };
  };

  const generateConfig = async (prompt) => {
    let res;
    for (let i = 0; i <= 3; i++) {
      res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${GROQ_API_KEY}` },
        body: JSON.stringify({
          model: 'llama-3.3-70b-versatile',
          max_tokens: 512,
          messages: [
            { role: 'system', content: 'Return ONLY a JSON array with one object containing: name (instrument name), toneHint (say the instrument name). No markdown.' },
            { role: 'user', content: prompt },
          ],
        }),
      });
      if (res.status === 429 && i < 3) { await sleep(1000 * (i + 1)); continue; }
      break;
    }
    if (!res.ok) throw new Error(`Groq error ${res.status}`);
    const data = await res.json();
    const text = data?.choices?.[0]?.message?.content ?? '';
    const cleaned = text.replace(/```json|```/g, '').trim();
    const s = cleaned.indexOf('['), e = cleaned.lastIndexOf(']');
    return JSON.parse(s !== -1 ? cleaned.slice(s, e + 1) : cleaned);
  };

  const makeFallbackBuffer = (note) => {
    const ctx = Tone.context.rawContext;
    const sr = ctx.sampleRate;
    const buf = ctx.createBuffer(1, sr * 2, sr);
    const data = buf.getChannelData(0);
    const freq = 440 * Math.pow(2, (noteToMidi(note) - 69) / 12);
    for (let i = 0; i < data.length; i++) {
      const t = i / sr;
      data[i] = Math.sin(2 * Math.PI * freq * t) * Math.max(0, 1 - t / 2);
    }
    return buf;
  };

  const generateAllSamples = async (toneHint) => {
    const bufferMap = {};
    setNoteStatuses(Object.fromEntries(SAMPLE_NOTES.map(n => [n, 'pending'])));
    for (let i = 0; i < SAMPLE_NOTES.length; i++) {
      const note = SAMPLE_NOTES[i];
      if (i > 0) await sleep(ELEVEN_DELAY_MS);
      setNoteStatuses(prev => ({ ...prev, [note]: 'loading' }));
      try {
        const res = await fetch('https://api.elevenlabs.io/v1/sound-generation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'xi-api-key': ELEVEN_API_KEY },
          body: JSON.stringify({ text: `${toneHint}, single note ${note}, isolated, no background noise`, duration_seconds: 2.0 }),
        });
        if (!res.ok) throw new Error();
        bufferMap[note] = await Tone.context.rawContext.decodeAudioData(await res.arrayBuffer());
        setNoteStatuses(prev => ({ ...prev, [note]: 'done' }));
      } catch {
        bufferMap[note] = makeFallbackBuffer(note);
        setNoteStatuses(prev => ({ ...prev, [note]: 'fallback' }));
      }
    }
    return bufferMap;
  };

  const buildSampler = (bufferMap, chain) => {
    const toneBuffers = {};
    for (const [note, raw] of Object.entries(bufferMap)) {
      toneBuffers[note] = new Tone.ToneAudioBuffer(raw);
    }
    return new Tone.Sampler({ urls: toneBuffers, attack: 0.005, release: 1.2 }).connect(chain.vol);
  };

const handleGenerate = async (prompt) => {
  await Tone.start(); // ✅ resume audio context first

  const genAudio = genAudioRef.current;
  console.log('genAudio ref:', genAudioRef.current);
    if (genAudio) {
      genAudio.loop = true;
      genAudio.currentTime = 0;
      genAudio.play().catch((e) => console.log('audio play failed:', e));
    }

    setStatus('loading');
    setLoadingMsg('Designing instrument...');
    setNoteStatuses({});

    try {
      const configs = await generateConfig(prompt);
      const config = configs[0];
      setLoadingMsg(`Generating samples for "${config.name}"...`);
      const bufferMap = await generateAllSamples(config.toneHint || 'clean tone');
      const idx = instruments.length;
      const chain = buildEffectsChain();
      samplersRef.current[idx] = buildSampler(bufferMap, chain);
      effectsChainRef.current[idx] = chain;
      setEffectsState(prev => [...prev, { ...DEFAULT_EFFECTS }]);
      setInstruments(prev => [...prev, config]);
      setCurrentIdx(idx);

      if (genAudio) { genAudio.pause(); genAudio.currentTime = 0; }

      setOverlay({ opacity: 1, background: 'white' });
      setTimeout(() => {
        setStatus('ready');
        setOverlay({ opacity: 0, background: 'white' });
      }, 800);

    } catch (e) {
      if (genAudio) { genAudio.pause(); genAudio.currentTime = 0; }
      setStatus('error');
      setLoadingMsg(e.message);
    }
  };

  const isPlaying = playbackId !== null;

  const playNote = useCallback((note) => {
    if (isPlaying) return;
    Tone.start();
    const sampler = samplersRef.current[currentIdx];
    if (!sampler || !sampler.loaded) return;
    sampler.triggerAttack(note);
    setPressedKeys(prev => new Set([...prev, note]));
    if (isRecording) {
      recordedRef.current.push({ type: 'attack', note, time: Tone.now() - recordStartRef.current });
    }
  }, [currentIdx, isRecording, isPlaying]);

  const releaseNote = useCallback((note) => {
    if (isPlaying) return;
    const sampler = samplersRef.current[currentIdx];
    if (!sampler) return;
    sampler.triggerRelease(note);
    setPressedKeys(prev => { const s = new Set(prev); s.delete(note); return s; });
    if (isRecording) {
      recordedRef.current.push({ type: 'release', note, time: Tone.now() - recordStartRef.current });
    }
  }, [currentIdx, isRecording, isPlaying]);

  const startRecording = () => {
    recordedRef.current = [];
    recordStartRef.current = Tone.now();
    setIsRecording(true);
  };

  const stopRecording = () => {
    setIsRecording(false);
    if (recordedRef.current.length > 0) {
      setRecordings(prev => [...prev, {
        id: Date.now(),
        events: [...recordedRef.current],
        instrumentIdx: currentIdx,
        instrumentName: instruments[currentIdx]?.name ?? 'unknown',
        label: `Take ${prev.length + 1}`,
      }]);
    }
  };

  const stopPlayback = () => {
    playbackTimersRef.current.forEach(clearTimeout);
    playbackTimersRef.current = [];
    setPlaybackId(null);
    setPlaybackKeys(new Set());
  };

  const playRecording = (rec) => {
    stopPlayback();
    setPlaybackId(rec.id);
    const sampler = samplersRef.current[rec.instrumentIdx];
    if (!sampler) return;
    rec.events.forEach(ev => {
      const t = setTimeout(() => {
        if (ev.type === 'attack') {
          sampler.triggerAttack(ev.note);
          setPlaybackKeys(prev => new Set([...prev, ev.note]));
        } else {
          sampler.triggerRelease(ev.note);
          setPlaybackKeys(prev => { const s = new Set(prev); s.delete(ev.note); return s; });
        }
      }, ev.time * 1000);
      playbackTimersRef.current.push(t);
    });
    const lastTime = rec.events[rec.events.length - 1]?.time ?? 0;
    playbackTimersRef.current.push(setTimeout(() => {
      setPlaybackId(null);
      setPlaybackKeys(new Set());
    }, (lastTime + 1.5) * 1000));
  };

  useEffect(() => {
    return () => {
      playbackTimersRef.current.forEach(clearTimeout);
      Object.values(samplersRef.current).forEach(s => s.dispose());
      Object.values(effectsChainRef.current).forEach(c => {
        c.vol.dispose(); c.reverb.dispose(); c.delay.dispose();
      });
    };
  }, []);

  const doneCount = Object.values(noteStatuses).filter(s => s === 'done' || s === 'fallback').length;
  const displayKeys = isPlaying ? playbackKeys : pressedKeys;

  return (
    <div className={styles.page}>
      <audio ref={genAudioRef} src="/samplegenerating.wav" />

      <div className={styles.header}>
        <button className={styles.backBtn} onClick={onBack}>← back</button>
        <div className={styles.headerTitle}>
          {instruments.length > 0 ? (
            <select className={styles.instSelect} value={currentIdx}
              onChange={e => setCurrentIdx(Number(e.target.value))}>
              {instruments.map((inst, i) => <option key={i} value={i}>{inst.name}</option>)}
            </select>
          ) : (
            <span className={styles.headerLabel}>instrument studio</span>
          )}
        </div>
        <div className={styles.statusPill} data-status={status}>{status}</div>
      </div>

      <div className={styles.body}>
        <div className={styles.sidebar}>
          <Chat onGenerate={handleGenerate} isLoading={status === 'loading'} />
        </div>

        <div className={styles.main}>
          {status === 'idle' && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>♩</div>
              <p>describe any instrument to begin</p>
            </div>
          )}

          {status === 'loading' && (
            <div className={styles.loadingArea}>
              <p className={styles.loadingMsg}>{loadingMsg}</p>
              {Object.keys(noteStatuses).length > 0 && (
                <>
                  <div className={styles.noteGrid}>
                    {SAMPLE_NOTES.map(note => (
                      <div key={note} className={styles.noteCell} data-state={noteStatuses[note] ?? 'pending'}>
                        <span className={styles.noteName}>{note}</span>
                        <span className={styles.noteIcon}>
                          {noteStatuses[note] === 'done' ? '✓' :
                           noteStatuses[note] === 'fallback' ? '~' :
                           noteStatuses[note] === 'loading' ? '…' : '·'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className={styles.progressTrack}>
                    <div className={styles.progressFill} style={{ width: `${(doneCount / SAMPLE_NOTES.length) * 100}%` }} />
                  </div>
                  <p className={styles.progressLabel}>{doneCount} / {SAMPLE_NOTES.length} samples</p>
                </>
              )}
            </div>
          )}

          {status === 'error' && <div className={styles.errorState}><p>{loadingMsg}</p></div>}

          {status === 'ready' && (
            <div className={styles.instrumentArea}>
              <p className={styles.instDesc}>{instruments[currentIdx]?.toneHint}</p>

              {isPlaying && (
                <div className={styles.playbackBanner}>
                  <span>playing back recording…</span>
                  <button onClick={stopPlayback} className={styles.stopPlaybackBtn}>■ stop</button>
                </div>
              )}

              <Keyboard
                onKeyPress={playNote}
                onKeyRelease={releaseNote}
                pressedKeys={displayKeys}
                disabled={isPlaying}
              />

              <div className={styles.recorderControls}>
                {!isRecording ? (
                  <button onClick={startRecording} className={styles.recStart} disabled={isPlaying}>
                    ● record
                  </button>
                ) : (
                  <button onClick={stopRecording} className={styles.recStop}>
                    ■ stop recording
                  </button>
                )}
              </div>

              {recordings.length > 0 && (
                <div className={styles.recordingsList}>
                  <p className={styles.recordingsHeader}>recordings</p>
                  {recordings.map(rec => (
                    <div key={rec.id} className={styles.recordingRow} data-playing={playbackId === rec.id}>
                      <div className={styles.recordingInfo}>
                        <span className={styles.recordingLabel}>{rec.label}</span>
                        <span className={styles.recordingInst}>{rec.instrumentName}</span>
                      </div>
                      <button
                        onClick={() => playbackId === rec.id ? stopPlayback() : playRecording(rec)}
                        className={styles.playBtn}
                      >
                        {playbackId === rec.id ? '■' : '▶'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {status === 'ready' && (
          <div className={styles.effectsSidebar}>
            <p className={styles.effectsTitle}>effects</p>
            {[
              { label: 'reverb', key: 'reverb', min: 0, max: 1, step: 0.01, fmt: v => `${Math.round(v * 100)}%` },
              { label: 'delay',  key: 'delay',  min: 0, max: 1, step: 0.01, fmt: v => `${Math.round(v * 100)}%` },
              { label: 'volume', key: 'volume', min: -30, max: 6, step: 1,   fmt: v => `${v}dB` },
            ].map(({ label, key, min, max, step, fmt }) => (
              <div key={key} className={styles.effectSlider}>
                <div className={styles.effectHeader}>
                  <span className={styles.effectLabel}>{label}</span>
                  <span className={styles.effectValue}>{fmt(currentEffects[key])}</span>
                </div>
                <div className={styles.sliderTrack}>
                  <div
                    className={styles.sliderFill}
                    style={{ height: `${((currentEffects[key] - min) / (max - min)) * 100}%` }}
                  />
                  <input
                    type="range"
                    min={min} max={max} step={step}
                    value={currentEffects[key]}
                    onChange={e => setCurrentEffect(key, Number(e.target.value))}
                    className={styles.sliderInput}
                    orient="vertical"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{
        position: 'fixed',
        inset: 0,
        background: overlay.background,
        opacity: overlay.opacity,
        transition: 'opacity 0.7s ease',
        pointerEvents: 'none',
        zIndex: 999,
      }} />
    </div>
  );
}